import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

export const COMPLIANCE_ITEMS = ["incarico", "ape", "visura_planimetria", "conformita", "provvigione"] as const;
export type ComplianceItemKey = (typeof COMPLIANCE_ITEMS)[number];

export const COMPLIANCE_LABEL: Record<ComplianceItemKey, string> = {
  incarico: "Incarico firmato",
  ape: "APE",
  visura_planimetria: "Visura e planimetria",
  conformita: "Conformità dichiarata",
  provvigione: "Provvigione per iscritto",
};

export const APE_CLASSES = ["A4", "A3", "A2", "A1", "B", "C", "D", "E", "F", "G"] as const;

const ComplianceItem = z.object({
  id: z.string().uuid().nullable(),
  listing_id: z.string().uuid(),
  item: z.enum(COMPLIANCE_ITEMS),
  status: z.enum(["manca", "fatto"]),
  expires_on: z.string().nullable(),
  esclusiva: z.boolean().nullable(),
  ape_class: z.string().nullable(),
  notes: z.string().nullable(),
  updated_at: z.string().nullable(),
});
export type ComplianceItem = z.infer<typeof ComplianceItem>;
const ComplianceResponse = z.object({ items: z.array(ComplianceItem) });

const SavedItem = ComplianceItem.extend({ id: z.string().uuid() });

const ExpiringItem = SavedItem.omit({ notes: true }).extend({
  notes: z.string().nullable().optional(),
  listing: z.object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.unknown().nullable(),
    city: z.string().nullable(),
  }).nullable(),
});
export type ExpiringItem = z.infer<typeof ExpiringItem>;
const ExpiringResponse = z.object({ items: z.array(ExpiringItem), today: z.string(), cutoff: z.string() });

export type ComplianceItemPatch = {
  status?: "manca" | "fatto";
  expires_on?: string | null;
  esclusiva?: boolean | null;
  ape_class?: string | null;
};

export function useListingCompliance(listingId: string | null) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["compliance", listingId],
    enabled: !!token && !!listingId,
    queryFn: () => apiFetch(`/api/v1/admin/listings/${listingId}/compliance`, token!, ComplianceResponse),
  });
}

export function usePatchComplianceItem() {
  const { session } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ listingId, item, ...input }: ComplianceItemPatch & { listingId: string; item: ComplianceItemKey }) =>
      apiFetch(`/api/v1/admin/listings/${listingId}/compliance/${item}`, session!.access_token, SavedItem, {
        method: "PATCH", body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["compliance"] });
      void qc.invalidateQueries({ queryKey: ["compliance-expiring"] });
      void qc.invalidateQueries({ queryKey: ["listings"] });
    },
  });
}

export function useComplianceExpiring(days = 60) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["compliance-expiring", days],
    enabled: !!token,
    queryFn: () => apiFetch("/api/v1/admin/compliance/expiring", token!, ExpiringResponse, { query: { days } }),
  });
}
